// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    // Use window.location.hostname instead of 'localhost' to support mobile testing
    // This allows the API to be accessed from other devices on the same network
    const protocol = window.location.protocol || 'http:';
    const hostname = window.location.hostname || 'localhost';
    return `${protocol}//${hostname}:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

const parseResponseBody = res => {
  const contentTypeHeader = res.headers.get('Content-Type');
  const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

  if (contentType === 'application/transit+json') {
    return res.text().then(deserialize);
  } else if (contentType === 'application/json') {
    return res.json();
  }

  return res.text().then(text => ({
    text,
    contentType,
  }));
};

const responseError = (res, data) => {
  const e = new Error();

  if (data && typeof data === 'object' && !Object.prototype.hasOwnProperty.call(data, 'text')) {
    return Object.assign(e, data);
  }

  const text = data && data.text ? data.text : '';
  const contentType = data && data.contentType ? data.contentType : null;
  e.status = res.status;
  e.statusText = res.statusText;
  e.message = `API request failed with ${res.status} ${res.statusText}`;
  e.apiError = {
    status: res.status,
    statusText: res.statusText,
    contentType,
    responsePreview: text.slice(0, 240),
  };

  return e;
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    return parseResponseBody(res).then(data => {
      if (res.status >= 400) {
        throw responseError(res, data);
      }

      return data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'text')
        ? data.text
        : data;
    });
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const API_CACHE_MAX_ENTRIES = 60;
const ticketApiCache = new Map();
const apiGetCache = new Map();

const normalizeDateForCacheKey = value => {
  if (typeof value !== 'string') {
    return value;
  }

  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return value;
  }

  const twoMinuteBucket = 2 * 60 * 1000;
  return new Date(Math.floor(timestamp / twoMinuteBucket) * twoMinuteBucket).toISOString();
};

const normalizeRequestBodyForCacheKey = body => {
  const normalizedBody = {};

  Object.keys(body || {})
    .sort()
    .forEach(key => {
      const value = body[key];
      if (value === undefined || value === null || value === '') {
        return;
      }

      normalizedBody[key] =
        key === 'startDateTime' || key === 'endDateTime' ? normalizeDateForCacheKey(value) : value;
    });

  return normalizedBody;
};

const pruneApiCache = cache => {
  if (cache.size <= API_CACHE_MAX_ENTRIES) {
    return;
  }

  const oldestKey = cache.keys().next().value;
  if (oldestKey) {
    cache.delete(oldestKey);
  }
};

const cachedPost = (path, body, options = {}) => {
  const { cacheTtl = 0, cacheKeyBody = body, ...requestOptions } = options;

  if (!cacheTtl) {
    return post(path, body, requestOptions);
  }

  const cacheKey = JSON.stringify({
    path,
    body: normalizeRequestBodyForCacheKey(cacheKeyBody),
  });
  const cachedEntry = ticketApiCache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry && cachedEntry.data && now - cachedEntry.timestamp < cacheTtl) {
    return Promise.resolve(cachedEntry.data);
  }

  if (cachedEntry?.promise) {
    return cachedEntry.promise;
  }

  const promise = post(path, body, requestOptions)
    .then(data => {
      ticketApiCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      pruneApiCache(ticketApiCache);
      return data;
    })
    .catch(error => {
      ticketApiCache.delete(cacheKey);
      throw error;
    });

  ticketApiCache.set(cacheKey, { promise, timestamp: now });
  return promise;
};

const cachedGet = (path, options = {}) => {
  const { cacheTtl = 0, ...requestOptions } = options;

  if (!cacheTtl) {
    return get(path, requestOptions);
  }

  const cacheKey = JSON.stringify({ path });
  const cachedEntry = apiGetCache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry && cachedEntry.data && now - cachedEntry.timestamp < cacheTtl) {
    return Promise.resolve(cachedEntry.data);
  }

  if (cachedEntry?.promise) {
    return cachedEntry.promise;
  }

  const promise = get(path, requestOptions)
    .then(data => {
      apiGetCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      pruneApiCache(apiGetCache);
      return data;
    })
    .catch(error => {
      apiGetCache.delete(cacheKey);
      throw error;
    });

  apiGetCache.set(cacheKey, { promise, timestamp: now });
  return promise;
};

// GET request helper
const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// PUT request helper
const put = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.PUT,
    body,
  };

  return request(path, requestOptions);
};

// DELETE request helper
const del = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.DELETE,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const submitTransactionHelpRequest = body => {
  return post('/api/transaction-help', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getTicketEvents = body => {
  return cachedPost('/api/ticket-events', body, {
    cacheTtl: 2 * 60 * 1000,
    cacheKeyBody: body,
  });
};

export const getCsvDiscoveryEvents = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.set(key, value);
    }
  });

  return cachedGet(`/api/supabase/csv-events/discovery?${queryParams.toString()}`, {
    cacheTtl: 2 * 60 * 1000,
  });
};

export const getCsvBatchDiscoveryEvents = (sections = []) =>
  cachedPost('/api/supabase/csv-events/batch-discovery', JSON.stringify({ sections }), {
    cacheTtl: 2 * 60 * 1000,
    cacheKeyBody: { sections },
    headers: { 'Content-Type': 'application/json' },
  });

export const getPublicLandingEventFeed = () =>
  cachedGet('/api/supabase/landing-event-feed/public', {
    headers: jsonHeaders,
    cacheTtl: 2 * 60 * 1000,
  });

// Personalized responses must not use the URL-only apiGetCache because the
// authenticated viewer can change while the browser tab remains mounted.
export const getPersonalizedLandingEventFeed = (options = {}) =>
  get('/api/supabase/landing-event-feed/personalized', {
    headers: jsonHeaders,
    ...(options.signal ? { signal: options.signal } : {}),
  });

export const getTicketAttractions = body => {
  return cachedPost('/api/ticket-attractions', body, {
    cacheTtl: 10 * 60 * 1000,
    cacheKeyBody: body,
  });
};

export const getTMAttractionEventCount = async attractionId => {
  const url = `${apiBaseUrl()}/api/ticket-attraction-event-count?attractionId=${encodeURIComponent(
    attractionId
  )}`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) return 0;
  const data = await response.json();
  return data?.count || 0;
};

export const getTicketVenues = body => {
  return cachedPost('/api/ticket-venues', body, {
    cacheTtl: 10 * 60 * 1000,
    cacheKeyBody: body,
  });
};

export const getTicketEventDetails = body => {
  return cachedPost('/api/ticket-event-details', body, {
    cacheTtl: 10 * 60 * 1000,
    cacheKeyBody: body,
  });
};

// Bids API endpoints (use JSON headers since backend returns JSON)
const jsonHeaders = { 'Content-Type': 'application/json' };

export const getPriceVerificationEmailPreviews = body => {
  return post('/api/price-verification/email-previews', JSON.stringify(body), {
    headers: jsonHeaders,
  });
};

export const getBids = (params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = queryParams ? `/api/supabase/bids?${queryParams}` : '/api/supabase/bids';
  return get(url, { headers: jsonHeaders });
};

export const getBidDetails = id => {
  return get(`/api/supabase/bids/${id}`, { headers: jsonHeaders });
};

export const resolveBidListing = id => {
  return get(`/api/supabase/bids/${id}/resolve-listing`, { headers: jsonHeaders });
};

export const getExternalPlatformLinks = params => {
  const queryParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.set(key, String(value));
    }
  });

  const suffix = queryParams.toString();
  const url = suffix ? `/api/external-platform-links?${suffix}` : '/api/external-platform-links';
  return get(url, { headers: jsonHeaders });
};

export const getExternalTickets = params => {
  const queryParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.set(key, String(value));
    }
  });
  const suffix = queryParams.toString();
  const url = suffix ? `/api/external-tickets?${suffix}` : '/api/external-tickets';
  return get(url, { headers: jsonHeaders });
};

export const getOpenAiMarketplacePrices = params => {
  const queryParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.set(key, String(value));
    }
  });
  const suffix = queryParams.toString();
  const url = suffix
    ? `/api/openai-marketplace-prices?${suffix}`
    : '/api/openai-marketplace-prices';
  return get(url, { headers: jsonHeaders });
};

export const getExaMarketplacePrices = params => {
  const queryParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.set(key, String(value));
    }
  });
  const suffix = queryParams.toString();
  const url = suffix
    ? `/api/exa-marketplace-prices?${suffix}`
    : '/api/exa-marketplace-prices';
  return get(url, { headers: jsonHeaders });
};

export const createBid = bidData => {
  // For JSON endpoints, we need to stringify the body
  return fetch(`${apiBaseUrl()}/api/supabase/bids`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bidData),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error(data.message || data.error);
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const updateBid = (id, bidData) => {
  return put(`/api/supabase/bids/${id}`, bidData, { headers: jsonHeaders });
};

export const deleteBid = (id, payload = null) => {
  return fetch(`${apiBaseUrl()}/api/supabase/bids/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: jsonHeaders,
    body: payload ? JSON.stringify(payload) : undefined,
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error(data.message || data.error);
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

// ================ User Preferences API ================ //

/**
 * Get user preferences (interests, location preferences, etc.)
 * @param {string} userId - The user ID
 * @returns {Promise} Response containing user preferences
 */
export const getUserPreferences = userId => {
  return get(`/api/supabase/users/${userId}/preferences`, { headers: jsonHeaders });
};

/**
 * Update user preferences (interests, location preferences, etc.)
 * @param {string} userId - The user ID
 * @param {Object} preferences - Preferences object
 * @param {Object} preferences.interests - Interest selections
 * @param {Array} preferences.interests.selected_main_categories - Main categories (e.g., ['music', 'sports'])
 * @param {Object} preferences.interests.selected_subcategories - Subcategories by category
 * @param {Object} preferences.location_preferences - Location preferences
 * @returns {Promise} Response containing updated preferences
 */
export const updateUserPreferences = (userId, preferences) => {
  return fetch(`${apiBaseUrl()}/api/supabase/users/${userId}/preferences`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error(data.message || data.error);
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

// ================ Event Recommendations API ================ //

/**
 * Get events related to a given CSV event.
 * Returns upcoming csv_events from the same catalog used by EventListingsPage,
 * prioritizing same attraction, then same venue, then same city.
 * @param {Object} options
 * @param {string} options.eventId - csv_events.id or ticketmaster_event_id
 * @param {number} options.limit - Max number of related events (default: 10)
 * @returns {Promise} Response containing related events
 */
export const getRelatedCsvEvents = (options = {}) => {
  const { eventId, limit = 10 } = options;
  const queryParams = new URLSearchParams({
    eventId: String(eventId),
    limit: limit.toString(),
  });

  return get(`/api/supabase/seatdata/events/related?${queryParams.toString()}`, {
    headers: jsonHeaders,
  });
};

/**
 * Get personalized event recommendations for the current user
 * @param {Object} options - Options for recommendations
 * @param {number} options.limit - Number of recommendations to fetch (default: 24)
 * @param {boolean} options.includeDiscovery - Include discovery recommendations (default: true)
 * @param {string} options.currentCategory - Optional category hint (e.g., current event category) to bias results
 * @returns {Promise} Response containing personalized recommendations
 */
export const getEventRecommendations = (options = {}) => {
  const { limit = 24, includeDiscovery = true, currentCategory } = options;

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    includeDiscovery: includeDiscovery.toString(),
  });

  if (currentCategory) {
    queryParams.append('currentCategory', currentCategory);
  }

  return get(`/api/recommendations/events?${queryParams.toString()}`, { headers: jsonHeaders });
};

export const getAuthenticatedLandingEvents = (options = {}) => {
  const { limit = 6 } = options;
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
  });

  return cachedGet(`/api/recommendations/landing-events?${queryParams.toString()}`, {
    headers: jsonHeaders,
    cacheTtl: 2 * 60 * 1000,
  });
};

export const getCrewHighlights = crewIds => {
  const uniqueCrewIds = Array.from(new Set((crewIds || []).map(id => String(id)).filter(Boolean)));
  const queryParams = new URLSearchParams({
    crew_ids: uniqueCrewIds.join(','),
  });

  return cachedGet(`/api/supabase/crews/highlights?${queryParams.toString()}`, {
    headers: jsonHeaders,
    cacheTtl: 2 * 60 * 1000,
  });
};
